import React, { useState, useEffect, useRef } from 'react';
import { useBeamSectionStore } from '../../hooks/useBeamSection';
import { SectionType, SectionGeometry } from '../../types/beamSection';
import { useModelStore } from '../../store/useModelStore';
import { usePropertyStore } from '../../store/usePropertyStore';
import type { PropertyDefinition, SectionType as PropertySectionType } from '../../types/property';
import * as THREE from 'three';
import { 
  getRolledSteelBeamByDesignation,
  getRolledSteelBeamDesignations,
  RolledSteelBeamData 
} from '../../data/sections/rolledSteelBeams';
import {
  getRolledSteelChannelByDesignation,
  getRolledSteelChannelDesignations,
  RolledSteelChannelData
} from '../../data/sections/rolledSteelChannels';
import {
  getRolledSteelEqualAngleByDesignation,
  getRolledSteelEqualAngleDesignations,
  RolledSteelEqualAngleData
} from '../../data/sections/rolledSteelEqualAngles';
import {
  getRolledSteelUnequalAngleByDesignation,
  getRolledSteelUnequalAngleDesignations,
  RolledSteelUnequalAngleData
} from '../../data/sections/rolledSteelUnequalAngles';
import {
  rolledSteelBulbAngles,
  RolledSteelBulbAngleData
} from '../../data/sections/rolledSteelBulbAngles';
import {
  getRolledSteelTeeBarByDesignation,
  rolledSteelTeeBars,
  RolledSteelTeeBarData
} from '../../data/sections/rolledSteelTeeBars';
import { 
  RolledSteelBulbAngleSection, 
  RolledSteelTeeBarSection 
} from '../../types/beamSection';

/**
 * Type guard to check if geometry is RolledSteelBulbAngleSection
 */
function isRolledBulbAngleSection(geometry: SectionGeometry): geometry is RolledSteelBulbAngleSection {
  return geometry.type === 'rolledBulbAngle';
}

/**
 * Type guard to check if geometry is RolledSteelTeeBarSection
 */
function isRolledTeeBarSection(geometry: SectionGeometry): geometry is RolledSteelTeeBarSection {
  return geometry.type === 'rolledTeeBar';
}

interface BeamSectionDialogProps {
  beamIds: string[];
  visible: boolean;
  position: { x: number; y: number };
  onClose: () => void;
}

/**
 * Section Configuration Dialog
 * Allows users to configure and apply sections (cuboid/cylinder) to beams
 * - Beam IDs are editable (comma-separated input)
 * - Length is auto-calculated from beam (non-editable)
 * - Only one section per beam
 * - Shows previous values if section exists
 * - Default colors: Purple for cuboid, Yellow for cylinder
 * - Dialog centers in canvas viewport
 * - Clear button removes section AND resets all parameters
 */
export const BeamSectionDialog: React.FC<BeamSectionDialogProps> = ({
  beamIds,
  visible,
  position,
  onClose,
}) => {
  const { setSection, getSectionForBeam, removeSectionsForBeams } = useBeamSectionStore();
  const { beams, nodes } = useModelStore();
  const { properties, addProperty, updateProperty, getProperty } = usePropertyStore();

  // Form state - beam IDs as editable comma-separated string
  const [beamIdsInput, setBeamIdsInput] = useState<string>('');
  const [sectionType, setSectionType] = useState<SectionType>('cuboid');
  const [color, setColor] = useState<string>('#9333EA'); // Purple for cuboid
  const [beamLength, setBeamLength] = useState<number>(5); // Auto-calculated from beam

  // Cuboid parameters (length is auto-calculated from beam)
  const [breadth, setBreadth] = useState<number>(0.3);
  const [height, setHeight] = useState<number>(0.5);

  // Cylinder parameters (length is auto-calculated from beam)
  const [radius, setRadius] = useState<number>(0.2);

  // Rolled Steel Beam parameters
  const [selectedDesignation, setSelectedDesignation] = useState<string>('');
  const [rolledSteelData, setRolledSteelData] = useState<RolledSteelBeamData | null>(null);

  // Rolled Steel Channel parameters
  const [selectedChannelDesignation, setSelectedChannelDesignation] = useState<string>('');
  const [rolledChannelData, setRolledChannelData] = useState<RolledSteelChannelData | null>(null);

  // Rolled Steel Equal Angle parameters
  const [selectedAngleDesignation, setSelectedAngleDesignation] = useState<string>('');
  const [rolledAngleData, setRolledAngleData] = useState<RolledSteelEqualAngleData | null>(null);

  // Rolled Steel Unequal Angle parameters
  const [selectedUnequalAngleDesignation, setSelectedUnequalAngleDesignation] = useState<string>('');
  const [rolledUnequalAngleData, setRolledUnequalAngleData] = useState<RolledSteelUnequalAngleData | null>(null);

  // Rolled Steel Bulb Angle parameters
  const [selectedBulbAngleDesignation, setSelectedBulbAngleDesignation] = useState<string>('');
  const [rolledBulbAngleData, setRolledBulbAngleData] = useState<RolledSteelBulbAngleData | null>(null);

  // Rolled Steel Tee Bar parameters
  const [selectedTeeBarDesignation, setSelectedTeeBarDesignation] = useState<string>('');
  const [rolledTeeBarData, setRolledTeeBarData] = useState<RolledSteelTeeBarData | null>(null);

  // Custom Section parameters
  const [customIx, setCustomIx] = useState<number>(0);
  const [customIy, setCustomIy] = useState<number>(0);
  const [customIz, setCustomIz] = useState<number>(0);
  const [customAx, setCustomAx] = useState<number>(0);
  const [customAy, setCustomAy] = useState<number>(0);
  const [customAz, setCustomAz] = useState<number>(0);
  const [customPerimeter, setCustomPerimeter] = useState<number>(0);
  const [customWidth, setCustomWidth] = useState<number>(0);
  const [customDepth, setCustomDepth] = useState<number>(0);
  const [customOrientation, setCustomOrientation] = useState<'horizontal' | 'vertical'>('horizontal');

  // UI state
  const [error, setError] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [hasExistingSection, setHasExistingSection] = useState<boolean>(false);
  const [existingSectionInfo, setExistingSectionInfo] = useState<string>('');

  // Orientation state
  const [flipHorizontal, setFlipHorizontal] = useState<boolean>(false); // Left-Right flip
  const [flipVertical, setFlipVertical] = useState<boolean>(false);     // Up-Down flip
  const [rotation, setRotation] = useState<number>(0);                  // Rotation in degrees (0-360)
  const [customRotation, setCustomRotation] = useState<string>('0');    // Custom rotation input

  // Draggable state
  const [dialogPosition, setDialogPosition] = useState(position);
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const dialogRef = useRef<HTMLDivElement>(null);

  // Initialize form with selected beam IDs
  useEffect(() => {
    if (visible && beamIds.length > 0) {
      // Set beam IDs as comma-separated string
      setBeamIdsInput(beamIds.join(', '));
      setError('');
      setSuccessMessage('');

      // Calculate beam length from first beam
      const firstBeamId = beamIds[0];
      const beam = beams.find((b) => b.id === firstBeamId);
      if (beam) {
        const startNode = nodes.find((n) => n.id === beam.startNodeId);
        const endNode = nodes.find((n) => n.id === beam.endNodeId);
        if (startNode && endNode) {
          const length = new THREE.Vector3(
            endNode.x - startNode.x,
            endNode.y - startNode.y,
            endNode.z - startNode.z
          ).length();
          setBeamLength(length);
        }
      }

      // Check if any beam already has a section - load those values
      const existingSection = getSectionForBeam(firstBeamId);
      if (existingSection) {
        // Mark that this beam has an existing section
        setHasExistingSection(true);
        
        // Load existing section values
        setColor(existingSection.color);
        
        // Load existing orientation values
        setRotation(existingSection.rotation || 0);
        setCustomRotation((existingSection.rotation || 0).toFixed(2));
        setFlipHorizontal(existingSection.flipHorizontal || false);
        setFlipVertical(existingSection.flipVertical || false);
        
        if (existingSection.geometry.type === 'cuboid') {
          setSectionType('cuboid');
          setBreadth(existingSection.geometry.breadth);
          setHeight(existingSection.geometry.height);
          setExistingSectionInfo(`Editing existing Cuboid section (${existingSection.geometry.breadth.toFixed(2)} × ${existingSection.geometry.height.toFixed(2)})`);
        } else if (existingSection.geometry.type === 'cylinder') {
          setSectionType('cylinder');
          setRadius(existingSection.geometry.radius);
          setExistingSectionInfo(`Editing existing Cylinder section (radius: ${existingSection.geometry.radius.toFixed(2)})`);
        } else if (existingSection.geometry.type === 'rolledSteel') {
          setSectionType('rolledSteel');
          setSelectedDesignation(existingSection.geometry.designation);
          const beamData = getRolledSteelBeamByDesignation(existingSection.geometry.designation);
          if (beamData) {
            setRolledSteelData(beamData);
          }
          setExistingSectionInfo(`Editing existing Rolled Steel section (${existingSection.geometry.designation})`);
        } else if (existingSection.geometry.type === 'rolledChannel') {
          setSectionType('rolledChannel');
          setSelectedChannelDesignation(existingSection.geometry.designation);
          const channelData = getRolledSteelChannelByDesignation(existingSection.geometry.designation);
          if (channelData) {
            setRolledChannelData(channelData);
          }
          setExistingSectionInfo(`Editing existing Rolled Steel Channel section (${existingSection.geometry.designation})`);
        } else if (existingSection.geometry.type === 'rolledEqualAngle') {
          setSectionType('rolledEqualAngle');
          setSelectedAngleDesignation(existingSection.geometry.designation);
          const angleData = getRolledSteelEqualAngleByDesignation(existingSection.geometry.designation);
          if (angleData) {
            setRolledAngleData(angleData);
          }
          setExistingSectionInfo(`Editing existing Rolled Steel Equal Angle section (${existingSection.geometry.designation})`);
        } else if (existingSection.geometry.type === 'rolledUnequalAngle') {
          setSectionType('rolledUnequalAngle');
          setSelectedUnequalAngleDesignation(existingSection.geometry.designation);
          const unequalAngleData = getRolledSteelUnequalAngleByDesignation(existingSection.geometry.designation);
          if (unequalAngleData) {
            setRolledUnequalAngleData(unequalAngleData);
          }
          setExistingSectionInfo(`Editing existing Rolled Steel Unequal Angle section (${existingSection.geometry.designation})`);
        } else if (isRolledBulbAngleSection(existingSection.geometry)) {
          setSectionType('rolledBulbAngle');
          // Type is now properly narrowed to RolledSteelBulbAngleSection
          const bulbGeometry = existingSection.geometry; // Preserve narrowed type
          setSelectedBulbAngleDesignation(bulbGeometry.designation);
          const bulbAngleData = rolledSteelBulbAngles.find(d => d.designation === bulbGeometry.designation);
          if (bulbAngleData) {
            setRolledBulbAngleData(bulbAngleData);
          }
          setExistingSectionInfo(`Editing existing Rolled Steel Bulb Angle section (${bulbGeometry.designation})`);
        } else if (isRolledTeeBarSection(existingSection.geometry)) {
          setSectionType('rolledTeeBar');
          // Type is now properly narrowed to RolledSteelTeeBarSection
          const teeGeometry = existingSection.geometry; // Preserve narrowed type
          setSelectedTeeBarDesignation(teeGeometry.designation);
          const teeBarData = getRolledSteelTeeBarByDesignation(teeGeometry.designation);
          if (teeBarData) {
            setRolledTeeBarData(teeBarData);
          }
          setExistingSectionInfo(`Editing existing Rolled Steel Tee Bar section (${teeGeometry.designation})`);
        }
      } else {
        // Reset to defaults for new section
        setHasExistingSection(false);
        setExistingSectionInfo('');
        setSectionType('cuboid');
        setColor('#9333EA'); // Purple
        setBreadth(0.3);
        setHeight(0.5);
        setRadius(0.2);
        setSelectedDesignation('');
        setRolledSteelData(null);
        setRotation(0);
        setCustomRotation('0');
        setFlipHorizontal(false);
        setFlipVertical(false);
        setSelectedChannelDesignation('');
        setRolledChannelData(null);
        setSelectedAngleDesignation('');
        setRolledAngleData(null);
        setSelectedUnequalAngleDesignation('');
        setRolledUnequalAngleData(null);
        setSelectedBulbAngleDesignation('');
        setRolledBulbAngleData(null);
        setSelectedTeeBarDesignation('');
        setRolledTeeBarData(null);
      }
    }
  }, [visible, beamIds, beams, nodes, getSectionForBeam]);

  // Center dialog in canvas viewport on open
  useEffect(() => {
    if (visible && dialogRef.current) {
      const dialogWidth = dialogRef.current.offsetWidth || 480;
      const dialogHeight = dialogRef.current.offsetHeight || 600;
      
      // Center in viewport
      const centerX = (window.innerWidth - dialogWidth) / 2;
      const centerY = (window.innerHeight - dialogHeight) / 2;
      
      setDialogPosition({
        x: Math.max(0, centerX),
        y: Math.max(0, centerY),
      });
    }
  }, [visible]);

  // Update color when section type changes (only if no existing section)
  useEffect(() => {
    if (visible && beamIds.length > 0) {
      const existingSection = getSectionForBeam(beamIds[0]);
      if (!existingSection) {
        // Set default colors only for new sections
        if (sectionType === 'cuboid') {
          setColor('#9333EA'); // Purple
        } else if (sectionType === 'cylinder') {
          setColor('#EAB308'); // Yellow
        } else if (sectionType === 'rolledSteel') {
          setColor('#DC2626'); // Red for rolled steel
        } else if (sectionType === 'rolledChannel') {
          setColor('#10B981'); // Green for rolled steel channel
        } else if (sectionType === 'rolledEqualAngle') {
          setColor('#F97316'); // Orange for rolled steel equal angle
        } else if (sectionType === 'rolledUnequalAngle') {
          setColor('#3B82F6'); // Blue for rolled steel unequal angle
        } else if (sectionType === 'rolledBulbAngle') {
          setColor('#00CED1'); // Cyan/Turquoise for rolled steel bulb angle
        } else if (sectionType === 'rolledTeeBar') {
          setColor('#FF1493'); // Deep Pink for rolled steel tee bar
        }
      }
    }
  }, [sectionType, visible, beamIds, getSectionForBeam]);

  // Handle rolled steel designation change
  useEffect(() => {
    if (selectedDesignation) {
      const beamData = getRolledSteelBeamByDesignation(selectedDesignation);
      if (beamData) {
        setRolledSteelData(beamData);
        // console.log('📋 Loaded rolled steel beam data:', beamData);
      }
    }
  }, [selectedDesignation, getRolledSteelBeamByDesignation]);

  // Handle rolled steel channel designation change
  useEffect(() => {
    if (selectedChannelDesignation) {
      const channelData = getRolledSteelChannelByDesignation(selectedChannelDesignation);
      if (channelData) {
        setRolledChannelData(channelData);
        console.log('📋 Loaded rolled steel channel data:', channelData);
      }
    }
  }, [selectedChannelDesignation, getRolledSteelChannelByDesignation]);

  // Handle rolled steel equal angle designation change
  useEffect(() => {
    if (selectedAngleDesignation) {
      const angleData = getRolledSteelEqualAngleByDesignation(selectedAngleDesignation);
      if (angleData) {
        setRolledAngleData(angleData);
        console.log('📋 Loaded rolled steel equal angle data:', angleData);
      }
    }
  }, [selectedAngleDesignation, getRolledSteelEqualAngleByDesignation]);
  
  // Handle rolled steel unequal angle designation change
  useEffect(() => {
    if (selectedUnequalAngleDesignation) {
      const unequalAngleData = getRolledSteelUnequalAngleByDesignation(selectedUnequalAngleDesignation);
      if (unequalAngleData) {
        setRolledUnequalAngleData(unequalAngleData);
        console.log('📋 Loaded rolled steel unequal angle data:', unequalAngleData);
      }
    }
  }, [selectedUnequalAngleDesignation, getRolledSteelUnequalAngleByDesignation]);
  
  // Handle rolled steel bulb angle designation change
  useEffect(() => {
    if (selectedBulbAngleDesignation) {
      const bulbAngleData = rolledSteelBulbAngles.find(d => d.designation === selectedBulbAngleDesignation);
      if (bulbAngleData) {
        setRolledBulbAngleData(bulbAngleData);
        console.log('📋 Loaded rolled steel bulb angle data:', bulbAngleData);
      }
    }
  }, [selectedBulbAngleDesignation]);

  // Handle rolled steel tee bar designation change
  useEffect(() => {
    if (selectedTeeBarDesignation) {
      const teeBarData = getRolledSteelTeeBarByDesignation(selectedTeeBarDesignation);
      if (teeBarData) {
        setRolledTeeBarData(teeBarData);
        console.log('📋 Loaded rolled steel tee bar data:', teeBarData);
      }
    }
  }, [selectedTeeBarDesignation, getRolledSteelTeeBarByDesignation]);

  // Handle drag functionality
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (dialogRef.current) {
      const rect = dialogRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
    }
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging && dialogRef.current) {
        const newX = e.clientX - dragOffset.x;
        const newY = e.clientY - dragOffset.y;

        const maxX = window.innerWidth - dialogRef.current.offsetWidth;
        const maxY = window.innerHeight - dialogRef.current.offsetHeight;

        setDialogPosition({
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
        });
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragOffset]);

  const handleApply = () => {
    setError('');

    // Parse beam IDs from comma-separated input
    const inputBeamIds = beamIdsInput
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);

    // console.log('🏗️ Applying section to beams:', inputBeamIds);

    if (inputBeamIds.length === 0) {
      setError('Please enter at least one beam ID');
      return;
    }

    // Validate all beam IDs exist
    const invalidBeams = inputBeamIds.filter(id => !beams.find(b => b.id === id));
    if (invalidBeams.length > 0) {
      setError('Beam(s) not found: ' + invalidBeams.join(', '));
      return;
    }

    // Get first beam for length calculation
    const firstBeam = beams.find((b) => b.id === inputBeamIds[0]);
    if (!firstBeam) {
      setError('Beam not found: ' + inputBeamIds[0]);
      return;
    }

    // Validate parameters
    if (sectionType === 'cuboid') {
      if (breadth <= 0 || height <= 0) {
        setError('Breadth and Height must be greater than 0');
        return;
      }
    } else if (sectionType === 'cylinder') {
      if (radius <= 0) {
        setError('Radius must be greater than 0');
        return;
      }
    } else if (sectionType === 'rolledSteel') {
      if (!selectedDesignation || !rolledSteelData) {
        setError('Please select a rolled steel beam designation');
        return;
      }
    } else if (sectionType === 'rolledChannel') {
      if (!selectedChannelDesignation || !rolledChannelData) {
        setError('Please select a rolled steel channel designation');
        return;
      }
    } else if (sectionType === 'rolledEqualAngle') {
      if (!selectedAngleDesignation || !rolledAngleData) {
        setError('Please select a rolled steel equal angle designation');
        return;
      }
    } else if (sectionType === 'rolledUnequalAngle') {
      if (!selectedUnequalAngleDesignation || !rolledUnequalAngleData) {
        setError('Please select a rolled steel unequal angle designation');
        return;
      }
    } else if (sectionType === 'rolledBulbAngle') {
      if (!selectedBulbAngleDesignation || !rolledBulbAngleData) {
        setError('Please select a rolled steel bulb angle designation');
        return;
      }
    } else if (sectionType === 'rolledTeeBar') {
      if (!selectedTeeBarDesignation || !rolledTeeBarData) {
        setError('Please select a rolled steel tee bar designation');
        return;
      }
    }

    // Create geometry based on type (using calculated beam length)
    let geometry: SectionGeometry;
    
    if (sectionType === 'cuboid') {
      geometry = {
        type: 'cuboid',
        length: beamLength,
        breadth,
        height,
      };
    } else if (sectionType === 'cylinder') {
      geometry = {
        type: 'cylinder',
        radius,
        length: beamLength,
      };
    } else if (sectionType === 'rolledSteel') {
      // Rolled Steel I-Beam
      geometry = {
        type: 'rolledSteel',
        designation: selectedDesignation,
        length: beamLength,
        h: rolledSteelData!.h,
        b: rolledSteelData!.b,
        tf: rolledSteelData!.tf,
        tw: rolledSteelData!.tw,
        r1: rolledSteelData!.r1,
        r2: rolledSteelData!.r2,
        D: rolledSteelData!.D,
        h1: rolledSteelData!.h1,
        h2: rolledSteelData!.h2,
        b1: rolledSteelData!.b1,
        C: rolledSteelData!.C,
        g: rolledSteelData!.g,
        g1Min: rolledSteelData!.g1Min,
        maxRivet: rolledSteelData!.maxRivet,
        w: rolledSteelData!.w,
        a: rolledSteelData!.a,
        Ixx: rolledSteelData!.Ixx,
        Iyy: rolledSteelData!.Iyy,
        rxx: rolledSteelData!.rxx,
        ryy: rolledSteelData!.ryy,
        Zxx: rolledSteelData!.Zxx,
        Zyy: rolledSteelData!.Zyy,
      };
    } else if (sectionType === 'rolledChannel') {
      // Rolled Steel Channel
      geometry = {
        type: 'rolledChannel',
        designation: selectedChannelDesignation,
        length: beamLength,
        h: rolledChannelData!.h,
        b: rolledChannelData!.b,
        tf: rolledChannelData!.tf,
        tw: rolledChannelData!.tw,
        r1: rolledChannelData!.r1,
        r2: rolledChannelData!.r2,
        D: rolledChannelData!.D,
        cyy: rolledChannelData!.cyy,
        h1: rolledChannelData!.h1,
        h2: rolledChannelData!.h2,
        b1: rolledChannelData!.b1,
        C: rolledChannelData!.C,
        g: rolledChannelData!.g,
        g1Min: rolledChannelData!.g1Min,
        maxRivet: rolledChannelData!.maxRivet,
        w: rolledChannelData!.w,
        a: rolledChannelData!.a,
        Ixx: rolledChannelData!.Ixx,
        Iyy: rolledChannelData!.Iyy,
        rxx: rolledChannelData!.rxx,
        ryy: rolledChannelData!.ryy,
        Zxx: rolledChannelData!.Zxx,
        Zyy: rolledChannelData!.Zyy,
      };
    } else if (sectionType === 'rolledEqualAngle') {
      // Rolled Steel Equal Angle
      geometry = {
        type: 'rolledEqualAngle',
        designation: selectedAngleDesignation,
        size: rolledAngleData!.size,
        length: beamLength,
        A: rolledAngleData!.A,
        B: rolledAngleData!.B,
        t: rolledAngleData!.t,
        r1: rolledAngleData!.r1,
        r2: rolledAngleData!.r2,
        Cxx: rolledAngleData!.Cxx,
        Cyy: rolledAngleData!.Cyy,
        exx: rolledAngleData!.exx,
        eyy: rolledAngleData!.eyy,
        w: rolledAngleData!.w,
        a: rolledAngleData!.a,
        Ixx: rolledAngleData!.Ixx,
        Iyy: rolledAngleData!.Iyy,
        Iuu: rolledAngleData!.Iuu,
        Ivv: rolledAngleData!.Ivv,
        Ixy: rolledAngleData!.Ixy,
        rxx: rolledAngleData!.rxx,
        ryy: rolledAngleData!.ryy,
        ruu: rolledAngleData!.ruu,
        rvv: rolledAngleData!.rvv,
        Zxx: rolledAngleData!.Zxx,
        Zyy: rolledAngleData!.Zyy,
      };
    } else if (sectionType === 'rolledUnequalAngle') {
      // Rolled Steel Unequal Angle
      geometry = {
        type: 'rolledUnequalAngle',
        designation: selectedUnequalAngleDesignation,
        size: rolledUnequalAngleData!.size,
        length: beamLength,
        A: rolledUnequalAngleData!.A,
        B: rolledUnequalAngleData!.B,
        t: rolledUnequalAngleData!.t,
        r1: rolledUnequalAngleData!.r1,
        r2: rolledUnequalAngleData!.r2,
        Cxx: rolledUnequalAngleData!.Cxx,
        Cyy: rolledUnequalAngleData!.Cyy,
        tanAlpha: rolledUnequalAngleData!.tanAlpha,
        exx: rolledUnequalAngleData!.exx,
        eyy: rolledUnequalAngleData!.eyy,
        w: rolledUnequalAngleData!.w,
        a: rolledUnequalAngleData!.a,
        Ixx: rolledUnequalAngleData!.Ixx,
        Iyy: rolledUnequalAngleData!.Iyy,
        Iuu: rolledUnequalAngleData!.Iuu,
        Ivv: rolledUnequalAngleData!.Ivv,
        Ixy: rolledUnequalAngleData!.Ixy,
        rxx: rolledUnequalAngleData!.rxx,
        ryy: rolledUnequalAngleData!.ryy,
        ruu: rolledUnequalAngleData!.ruu,
        rvv: rolledUnequalAngleData!.rvv,
        Zxx: rolledUnequalAngleData!.Zxx,
        Zyy: rolledUnequalAngleData!.Zyy,
      };
    } else if (sectionType === 'rolledBulbAngle') {
      // Rolled Steel Bulb Angle
      geometry = {
        type: 'rolledBulbAngle',
        designation: selectedBulbAngleDesignation,
        size: rolledBulbAngleData!.size,
        length: beamLength,
        h: rolledBulbAngleData!.h,
        b: rolledBulbAngleData!.b,
        tw: rolledBulbAngleData!.tw,
        tf: rolledBulbAngleData!.tf,
        D: rolledBulbAngleData!.D,
        r1: rolledBulbAngleData!.r1,
        r2: rolledBulbAngleData!.r2,
        r3: rolledBulbAngleData!.r3,
        Cxx: rolledBulbAngleData!.Cxx,
        Cyy: rolledBulbAngleData!.Cyy,
        tanAlpha: rolledBulbAngleData!.tanAlpha,
        exx: rolledBulbAngleData!.exx,
        eyy: rolledBulbAngleData!.eyy,
        w: rolledBulbAngleData!.w,
        a: rolledBulbAngleData!.a,
        Ixx: rolledBulbAngleData!.Ixx,
        Iyy: rolledBulbAngleData!.Iyy,
        Iuu: rolledBulbAngleData!.Iuu,
        Ivv: rolledBulbAngleData!.Ivv,
        rxx: rolledBulbAngleData!.rxx,
        ryy: rolledBulbAngleData!.ryy,
        ruu: rolledBulbAngleData!.ruu,
        rvv: rolledBulbAngleData!.rvv,
        Zxx: rolledBulbAngleData!.Zxx,
        Zyy: rolledBulbAngleData!.Zyy,
      };
    } else if (sectionType === 'rolledTeeBar') {
      // Rolled Steel Tee Bar
      geometry = {
        type: 'rolledTeeBar',
        designation: selectedTeeBarDesignation,
        length: beamLength,
        h: rolledTeeBarData!.h,
        b: rolledTeeBarData!.b,
        tf: rolledTeeBarData!.tf,
        tw: rolledTeeBarData!.tw,
        r1: rolledTeeBarData!.r1,
        r2: rolledTeeBarData!.r2,
        D: rolledTeeBarData!.D,
        Cxx: rolledTeeBarData!.Cxx,
        w: rolledTeeBarData!.w,
        a: rolledTeeBarData!.a,
        Ixx: rolledTeeBarData!.Ixx,
        Iyy: rolledTeeBarData!.Iyy,
        rxx: rolledTeeBarData!.rxx,
        ryy: rolledTeeBarData!.ryy,
        Zxx: rolledTeeBarData!.Zxx,
        Zyy: rolledTeeBarData!.Zyy,
      };
    } else {
      // Custom Section
      geometry = {
        type: 'customSection',
        length: beamLength,
        Ix: customIx,
        Iy: customIy,
        Iz: customIz,
        Ax: customAx,
        Ay: customAy,
        Az: customAz,
        perimeter: customPerimeter,
        width: customWidth > 0 ? customWidth : undefined,
        depth: customDepth > 0 ? customDepth : undefined,
        orientation: (customWidth > 0 && customDepth > 0) ? customOrientation : undefined,
      };
    }

    // Apply section to each beam individually (one section per beam)
    inputBeamIds.forEach((beamId) => {
      const sectionId = `section_${beamId}_${Date.now()}`;

      // Save section (one section per beam)
      setSection(sectionId, {
        beamIds: [beamId],
        geometry,
        color,
        rotation,
        flipHorizontal,
        flipVertical,
        createdAt: Date.now(),
        updatedAt: Date.now(),
      });
    });

    // Add/update property in PropertyStore
    syncSectionToPropertyStore(geometry, inputBeamIds);

    setSuccessMessage(`✅ Section applied to ${inputBeamIds.length} beam(s)!`);
    setTimeout(() => {
      setSuccessMessage('');
    }, 3000);
  };

  /**
   * Sync applied beam section to Property Store
   * Creates a new property or updates existing one with matching section
   */
  const syncSectionToPropertyStore = (geometry: SectionGeometry, beamIds: string[]) => {
    // Convert section geometry to property section type and properties
    let propertySectionType: PropertySectionType | undefined;
    let sectionProperties: any = {};
    let designation = '';

    // Map beam section type to property section type
    if (geometry.type === 'rolledSteel') {
      propertySectionType = 'I-Beam';
      designation = geometry.designation;
      sectionProperties = {
        designation: geometry.designation,
        depth: geometry.h,
        width: geometry.b,
        flangeThickness: geometry.tf,
        webThickness: geometry.tw,
        area: geometry.a,
        momentOfInertiaY: geometry.Ixx,
        momentOfInertiaZ: geometry.Iyy,
        radiusOfGyrationY: geometry.rxx,
        radiusOfGyrationZ: geometry.ryy,
        sectionModulusY: geometry.Zxx,
        sectionModulusZ: geometry.Zyy,
        weight: geometry.w,
      };
    } else if (geometry.type === 'rolledChannel') {
      propertySectionType = 'Channel';
      designation = geometry.designation;
      sectionProperties = {
        designation: geometry.designation,
        depth: geometry.h,
        width: geometry.b,
        flangeThickness: geometry.tf,
        webThickness: geometry.tw,
        area: geometry.a,
        momentOfInertiaY: geometry.Ixx,
        momentOfInertiaZ: geometry.Iyy,
        radiusOfGyrationY: geometry.rxx,
        radiusOfGyrationZ: geometry.ryy,
        sectionModulusY: geometry.Zxx,
        sectionModulusZ: geometry.Zyy,
        weight: geometry.w,
      };
    } else if (geometry.type === 'rolledEqualAngle') {
      propertySectionType = 'Equal Angle';
      designation = geometry.designation;
      sectionProperties = {
        designation: geometry.designation,
        depth: geometry.A,
        width: geometry.B,
        webThickness: geometry.t,
        area: geometry.a,
        momentOfInertiaY: geometry.Ixx,
        momentOfInertiaZ: geometry.Iyy,
        radiusOfGyrationY: geometry.rxx,
        radiusOfGyrationZ: geometry.ryy,
        sectionModulusY: geometry.Zxx,
        sectionModulusZ: geometry.Zyy,
        weight: geometry.w,
      };
    } else if (geometry.type === 'rolledUnequalAngle') {
      propertySectionType = 'Unequal Angle';
      designation = geometry.designation;
      sectionProperties = {
        designation: geometry.designation,
        depth: geometry.A,
        width: geometry.B,
        webThickness: geometry.t,
        area: geometry.a,
        momentOfInertiaY: geometry.Ixx,
        momentOfInertiaZ: geometry.Iyy,
        radiusOfGyrationY: geometry.rxx,
        radiusOfGyrationZ: geometry.ryy,
        sectionModulusY: geometry.Zxx,
        sectionModulusZ: geometry.Zyy,
        weight: geometry.w,
      };
    } else if (geometry.type === 'rolledBulbAngle') {
      propertySectionType = 'Bulb Angle';
      designation = geometry.designation;
      sectionProperties = {
        designation: geometry.designation,
        depth: geometry.h,
        width: geometry.b,
        flangeThickness: geometry.tf,
        webThickness: geometry.tw,
        area: geometry.a,
        momentOfInertiaY: geometry.Ixx,
        momentOfInertiaZ: geometry.Iyy,
        radiusOfGyrationY: geometry.rxx,
        radiusOfGyrationZ: geometry.ryy,
        sectionModulusY: geometry.Zxx,
        sectionModulusZ: geometry.Zyy,
        weight: geometry.w,
      };
    } else if (geometry.type === 'rolledTeeBar') {
      propertySectionType = 'Tee Bar';
      designation = geometry.designation;
      sectionProperties = {
        designation: geometry.designation,
        depth: geometry.h,
        width: geometry.b,
        flangeThickness: geometry.tf,
        webThickness: geometry.tw,
        area: geometry.a,
        momentOfInertiaY: geometry.Ixx,
        momentOfInertiaZ: geometry.Iyy,
        radiusOfGyrationY: geometry.rxx,
        radiusOfGyrationZ: geometry.ryy,
        sectionModulusY: geometry.Zxx,
        sectionModulusZ: geometry.Zyy,
        weight: geometry.w,
      };
    } else if (geometry.type === 'customSection') {
      propertySectionType = 'Custom Section';
      designation = 'Custom';
      sectionProperties = {
        designation: 'Custom Section',
        momentOfInertiaY: geometry.Ix,
        momentOfInertiaZ: geometry.Iy,
        area: geometry.Ax,
        depth: geometry.depth,
        width: geometry.width,
      };
    } else {
      // Cuboid and Cylinder don't map to section properties table
      return;
    }

    // Check if an identical property already exists
    const existingProperty = properties.find((prop) => {
      if (prop.sectionType !== propertySectionType) return false;
      if (prop.material !== 'Steel') return false;
      
      // For rolled sections, match by designation
      if (designation && prop.sectionProperties?.designation === designation) {
        return true;
      }
      
      // For custom sections, match by key properties
      if (propertySectionType === 'Custom Section') {
        const props = prop.sectionProperties;
        return (
          props?.momentOfInertiaY === geometry.Ix &&
          props?.momentOfInertiaZ === geometry.Iy &&
          props?.area === geometry.Ax
        );
      }
      
      return false;
    });

    if (existingProperty) {
      // Property exists - add beam IDs to it
      const updatedBeamIds = Array.from(new Set([...existingProperty.beamIds, ...beamIds]));
      updateProperty(existingProperty.P_ID, { beamIds: updatedBeamIds });
      console.log(`✅ Added beams to existing property ${existingProperty.P_ID}:`, beamIds);
    } else {
      // Create new property
      const newPropertyId = `P${properties.length + 1}`;
      const newProperty: PropertyDefinition = {
        P_ID: newPropertyId,
        material: 'Steel',
        sectionType: propertySectionType,
        sectionProperties,
        beamIds: [...beamIds],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      
      addProperty(newProperty);
      console.log(`✅ Created new property ${newPropertyId}:`, beamIds);
    }
  };

  const handleClear = () => {
    // Parse beam IDs from input
    const inputBeamIds = beamIdsInput
      .split(',')
      .map(id => id.trim())
      .filter(id => id.length > 0);

    // Remove all sections from the selected beam(s)
    if (inputBeamIds.length > 0) {
      removeSectionsForBeams(inputBeamIds);
      // console.log('🗑️ Cleared sections from', inputBeamIds.length, 'beam(s)');
      setSuccessMessage(`✅ Section removed from ${inputBeamIds.length} beam(s)!`);
      setTimeout(() => {
        setSuccessMessage('');
      }, 2000);
    }
    
    // Reset ALL parameters to defaults
    setSectionType('cuboid');
    setBreadth(0.3);
    setHeight(0.5);
    setRadius(0.2);
    setSelectedDesignation('');
    setRolledSteelData(null);
    setSelectedChannelDesignation('');
    setRolledChannelData(null);
    setSelectedAngleDesignation('');
    setRolledAngleData(null);
    setSelectedUnequalAngleDesignation('');
    setRolledUnequalAngleData(null);
    setColor('#9333EA'); // Purple for cuboid
    setBeamLength(5);
    setRotation(0);
    setCustomRotation('0');
    setFlipHorizontal(false);
    setFlipVertical(false);
    setError('');
  };

  const handleCancel = () => {
    setError('');
    setSuccessMessage('');
    onClose();
  };

  if (!visible) return null;

  return (
    <div
      ref={dialogRef}
      className={`fixed bg-white rounded-lg shadow-xl w-[576px] z-50 ${
        isDragging ? 'cursor-grabbing shadow-2xl' : ''
      }`}
      style={{
        left: dialogPosition.x + 'px',
        top: dialogPosition.y + 'px',
      }}
    >
      {/* Draggable Header */}
      <div
        className={`bg-blue-600 text-white px-4 py-3 rounded-t-lg flex justify-between items-center ${
          isDragging ? 'cursor-grabbing' : 'cursor-grab'
        } select-none`}
        onMouseDown={handleMouseDown}
      >
        <h3 className="text-xl font-semibold">Section Configuration</h3>
        <button
          onClick={handleCancel}
          className="text-white hover:bg-blue-700 rounded px-2 py-1 transition-colors"
          onMouseDown={(e) => e.stopPropagation()}
        >
          ✕
        </button>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="space-y-4">
          {/* Existing Section Info Banner */}
          {hasExistingSection && existingSectionInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
              <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-blue-900">
                  {existingSectionInfo}
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Applying will replace the current section. Use "Clear" to remove it.
                </p>
              </div>
            </div>
          )}

          {/* Beam IDs Input (Editable, comma-separated) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Beam IDs (comma-separated)
            </label>
            <input
              type="text"
              value={beamIdsInput}
              onChange={(e) => setBeamIdsInput(e.target.value)}
              placeholder="e.g., 1, 2, 3"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
            />
            <p className="text-xs text-gray-500 mt-1">
              Add, remove, or change beam IDs • One section per beam • Length: {beamLength.toFixed(2)} units
            </p>
          </div>

          {/* Section Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Section Type
            </label>
            <select
              value={sectionType}
              onChange={(e) => setSectionType(e.target.value as SectionType)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
            >
              <option value="cuboid">Cuboid (Purple)</option>
              <option value="customSection">Custom Section</option>
              <option value="cylinder">Cylinder (Yellow)</option>
              <option value="rolledBulbAngle">Rolled Steel Bulb Angle (Cyan)</option>
              <option value="rolledChannel">Rolled Steel Channel (Green)</option>
              <option value="rolledEqualAngle">Rolled Steel Equal Angle (Orange)</option>
              <option value="rolledSteel">Rolled Steel I-Beam (Red)</option>
              <option value="rolledTeeBar">Rolled Steel Tee Bar (Deep Pink)</option>
              <option value="rolledUnequalAngle">Rolled Steel Unequal Angle (Blue)</option>
            </select>
          </div>

          {/* Rolled Steel Beam Selection */}
          {sectionType === 'rolledSteel' && (
            <div className="space-y-3 p-4 bg-gray-50 rounded border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">Rolled Steel I-Beam Selection</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Designation
                  </label>
                  <select
                    value={selectedDesignation}
                    onChange={(e) => setSelectedDesignation(e.target.value)}
                    className="w-full px-3 py-2 text-sm border text-black border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                  >
                    <option value="">Select a designation...</option>
                    {getRolledSteelBeamDesignations().map((designation) => (
                      <option key={designation} value={designation}>
                        {designation}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Display selected beam properties */}
                {rolledSteelData && (
                  <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">
                      {rolledSteelData.designation} - Properties
                    </h5>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Weight:</span>
                        <span className="font-medium text-gray-600">{rolledSteelData.w} kg/m</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Area:</span>
                        <span className="font-medium text-gray-600">{rolledSteelData.a} cm²</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Depth (h):</span>
                        <span className="font-medium text-gray-600">{rolledSteelData.h} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Width (b):</span>
                        <span className="font-medium text-gray-600">{rolledSteelData.b} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Flange (tf):</span>
                        <span className="font-medium text-gray-600">{rolledSteelData.tf} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Web (tw):</span>
                        <span className="font-medium text-gray-600">{rolledSteelData.tw} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Root Radius (r1):</span>
                        <span className="font-medium text-gray-600">{rolledSteelData.r1} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Toe Radius (r2):</span>
                        <span className="font-medium text-gray-600">{rolledSteelData.r2} mm</span>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-500 italic">
                        Length will match beam: {beamLength.toFixed(2)} units
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rolled Steel Channel Selection */}
          {sectionType === 'rolledChannel' && (
            <div className="space-y-3 p-4 bg-gray-50 rounded border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">Rolled Steel C-Channel Selection</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Designation
                  </label>
                  <select
                    value={selectedChannelDesignation}
                    onChange={(e) => setSelectedChannelDesignation(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-black bg-white"
                    style={{ color: '#000' }}
                  >
                    <option value="" className="text-black">Select a designation...</option>
                    {getRolledSteelChannelDesignations().map((designation) => (
                      <option key={designation} value={designation} className="text-black">
                        {designation}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Display selected channel properties */}
                {rolledChannelData && (
                  <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">
                      {rolledChannelData.designation} - Properties
                    </h5>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Weight:</span>
                        <span className="font-medium text-gray-600">{rolledChannelData.w} kg/m</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Area:</span>
                        <span className="font-medium text-gray-600">{rolledChannelData.a} cm²</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Depth (h):</span>
                        <span className="font-medium text-gray-600">{rolledChannelData.h} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Width (b):</span>
                        <span className="font-medium text-gray-600">{rolledChannelData.b} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Flange (tf):</span>
                        <span className="font-medium text-gray-600">{rolledChannelData.tf} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Web (tw):</span>
                        <span className="font-medium text-gray-600">{rolledChannelData.tw} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Root Radius (r1):</span>
                        <span className="font-medium text-gray-600">{rolledChannelData.r1} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Centroid (cyy):</span>
                        <span className="font-medium text-gray-600">{rolledChannelData.cyy} cm</span>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-500 italic">
                        Length will match beam: {beamLength.toFixed(2)} units
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rolled Steel Equal Angle Selection */}
          {sectionType === 'rolledEqualAngle' && (
            <div className="space-y-3 p-4 bg-gray-50 rounded border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">Rolled Steel Equal Angle Selection</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Designation
                  </label>
                  <select
                    value={selectedAngleDesignation}
                    onChange={(e) => setSelectedAngleDesignation(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-black bg-white"
                    style={{ color: '#000' }}
                  >
                    <option value="" className="text-black">Select a designation...</option>
                    {getRolledSteelEqualAngleDesignations().map((designation) => (
                      <option key={designation} value={designation} className="text-black">
                        {designation}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Display selected equal angle properties */}
                {rolledAngleData && (
                  <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">
                      {rolledAngleData.designation} - Properties
                    </h5>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Weight:</span>
                        <span className="font-medium text-gray-600">{rolledAngleData.w} kg/m</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Area:</span>
                        <span className="font-medium text-gray-600">{rolledAngleData.a} cm²</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Size (A × B):</span>
                        <span className="font-medium text-gray-600">{rolledAngleData.size} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Leg A:</span>
                        <span className="font-medium text-gray-600">{rolledAngleData.A} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Leg B:</span>
                        <span className="font-medium text-gray-600">{rolledAngleData.B} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Thickness (t):</span>
                        <span className="font-medium text-gray-600">{rolledAngleData.t} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Root Radius (r1):</span>
                        <span className="font-medium text-gray-600">{rolledAngleData.r1} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Toe Radius (r2):</span>
                        <span className="font-medium text-gray-600">{rolledAngleData.r2} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Centroid Cxx:</span>
                        <span className="font-medium text-gray-600">{rolledAngleData.Cxx} cm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Centroid Cyy:</span>
                        <span className="font-medium text-gray-600">{rolledAngleData.Cyy} cm</span>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-500 italic">
                        Length will match beam: {beamLength.toFixed(2)} units
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rolled Steel Unequal Angle Selection */}
          {sectionType === 'rolledUnequalAngle' && (
            <div className="space-y-3 p-4 bg-gray-50 rounded border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">Rolled Steel Unequal Angle Selection</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Designation
                  </label>
                  <select
                    value={selectedUnequalAngleDesignation}
                    onChange={(e) => setSelectedUnequalAngleDesignation(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-black bg-white"
                    style={{ color: '#000' }}
                  >
                    <option value="" className="text-black">Select a designation...</option>
                    {getRolledSteelUnequalAngleDesignations().map((designation) => (
                      <option key={designation} value={designation} className="text-black">
                        {designation}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Display selected unequal angle properties */}
                {rolledUnequalAngleData && (
                  <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">
                      {rolledUnequalAngleData.designation} - Properties (10 Variables)
                    </h5>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Weight:</span>
                        <span className="font-medium text-gray-600">{rolledUnequalAngleData.w} kg/m</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Area:</span>
                        <span className="font-medium text-gray-600">{rolledUnequalAngleData.a} cm²</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Size (A × B):</span>
                        <span className="font-medium text-gray-600">{rolledUnequalAngleData.size} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Major Leg (A):</span>
                        <span className="font-medium text-gray-600">{rolledUnequalAngleData.A} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Minor Leg (B):</span>
                        <span className="font-medium text-gray-600">{rolledUnequalAngleData.B} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Thickness (t):</span>
                        <span className="font-medium text-gray-600">{rolledUnequalAngleData.t} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Root Radius (r1):</span>
                        <span className="font-medium text-gray-600">{rolledUnequalAngleData.r1} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Toe Radius (r2):</span>
                        <span className="font-medium text-gray-600">{rolledUnequalAngleData.r2} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Centroid Cxx:</span>
                        <span className="font-medium text-gray-600">{rolledUnequalAngleData.Cxx} cm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Centroid Cyy:</span>
                        <span className="font-medium text-gray-600">{rolledUnequalAngleData.Cyy} cm</span>
                      </div>
                      <div className="flex justify-between col-span-2 bg-blue-50 px-2 py-1 rounded">
                        <span className="text-gray-700 font-semibold">tan(α):</span>
                        <span className="font-bold text-blue-700">{rolledUnequalAngleData.tanAlpha}</span>
                      </div>
                      <div className="flex justify-between col-span-2 text-xs italic text-gray-500 mt-1">
                        <span>Principal Axis Angle α:</span>
                        <span>{(Math.atan(rolledUnequalAngleData.tanAlpha) * 180 / Math.PI).toFixed(1)}°</span>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-500 italic">
                        Length will match beam: {beamLength.toFixed(2)} units
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rolled Steel Bulb Angle Selection */}
          {sectionType === 'rolledBulbAngle' && (
            <div className="space-y-3 p-4 bg-gray-50 rounded border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">Rolled Steel Bulb Angle Selection</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Designation
                  </label>
                  <select
                    value={selectedBulbAngleDesignation}
                    onChange={(e) => setSelectedBulbAngleDesignation(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-black bg-white"
                    style={{ color: '#000' }}
                  >
                    <option value="" className="text-black">Select a designation...</option>
                    {rolledSteelBulbAngles.map((bulbAngle) => (
                      <option key={bulbAngle.designation} value={bulbAngle.designation} className="text-black">
                        {bulbAngle.designation}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Display selected bulb angle properties */}
                {rolledBulbAngleData && (
                  <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">
                      {rolledBulbAngleData.designation} - Properties (11 Variables)
                    </h5>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Weight:</span>
                        <span className="font-medium text-gray-600">{rolledBulbAngleData.w} kg/m</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Area:</span>
                        <span className="font-medium text-gray-600">{rolledBulbAngleData.a} cm²</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Size (h × b):</span>
                        <span className="font-medium text-gray-600">{rolledBulbAngleData.size} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Depth (h):</span>
                        <span className="font-medium text-gray-600">{rolledBulbAngleData.h} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Width (b):</span>
                        <span className="font-medium text-gray-600">{rolledBulbAngleData.b} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Web Thick. (tw):</span>
                        <span className="font-medium text-gray-600">{rolledBulbAngleData.tw} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Flange Thick. (tf):</span>
                        <span className="font-medium text-gray-600">{rolledBulbAngleData.tf} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Bulb Diameter (D):</span>
                        <span className="font-medium text-gray-600">{rolledBulbAngleData.D} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Root Radius (r1):</span>
                        <span className="font-medium text-gray-600">{rolledBulbAngleData.r1} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Toe Radius (r2):</span>
                        <span className="font-medium text-gray-600">{rolledBulbAngleData.r2} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Internal Radius (r3):</span>
                        <span className="font-medium text-gray-600">{rolledBulbAngleData.r3} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Centroid Cxx:</span>
                        <span className="font-medium text-gray-600">{rolledBulbAngleData.Cxx} cm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Centroid Cyy:</span>
                        <span className="font-medium text-gray-600">{rolledBulbAngleData.Cyy} cm</span>
                      </div>
                      <div className="flex justify-between col-span-2 bg-cyan-50 px-2 py-1 rounded">
                        <span className="text-gray-700 font-semibold">tan(α):</span>
                        <span className="font-bold text-cyan-700">{rolledBulbAngleData.tanAlpha}</span>
                      </div>
                      <div className="flex justify-between col-span-2 text-xs italic text-gray-500 mt-1">
                        <span>Principal Axis Angle α:</span>
                        <span>{(Math.atan(rolledBulbAngleData.tanAlpha) * 180 / Math.PI).toFixed(1)}°</span>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-500 italic">
                        Length will match beam: {beamLength.toFixed(2)} units
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rolled Steel Tee Bar Selection */}
          {sectionType === 'rolledTeeBar' && (
            <div className="space-y-3 p-4 bg-gray-50 rounded border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">Rolled Steel Tee Bar Selection</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Designation
                  </label>
                  <select
                    value={selectedTeeBarDesignation}
                    onChange={(e) => setSelectedTeeBarDesignation(e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-black bg-white"
                    style={{ color: '#000' }}
                  >
                    <option value="" className="text-black">Select a designation...</option>
                    {rolledSteelTeeBars.map((teeBar) => (
                      <option key={teeBar.designation} value={teeBar.designation} className="text-black">
                        {teeBar.designation}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Display selected tee bar properties */}
                {rolledTeeBarData && (
                  <div className="mt-3 p-3 bg-white rounded border border-gray-200">
                    <h5 className="text-xs font-semibold text-gray-700 mb-2">
                      {rolledTeeBarData.designation} - Properties (8 Variables)
                    </h5>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Weight:</span>
                        <span className="font-medium text-gray-600">{rolledTeeBarData.w} kg/m</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Area:</span>
                        <span className="font-medium text-gray-600">{rolledTeeBarData.a} cm²</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Depth (h):</span>
                        <span className="font-medium text-gray-600">{rolledTeeBarData.h} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Width (b):</span>
                        <span className="font-medium text-gray-600">{rolledTeeBarData.b} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Flange Thick. (tf):</span>
                        <span className="font-medium text-gray-600">{rolledTeeBarData.tf} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Web Thick. (tw):</span>
                        <span className="font-medium text-gray-600">{rolledTeeBarData.tw} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Root Radius (r1):</span>
                        <span className="font-medium text-gray-600">{rolledTeeBarData.r1} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Toe Radius (r2):</span>
                        <span className="font-medium text-gray-600">{rolledTeeBarData.r2} mm</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Flange Slope (D):</span>
                        <span className="font-medium text-gray-600">{rolledTeeBarData.D}°</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Centroid Cxx:</span>
                        <span className="font-medium text-gray-600">{rolledTeeBarData.Cxx} cm</span>
                      </div>
                      <div className="flex justify-between col-span-2 bg-pink-50 px-2 py-1 rounded">
                        <span className="text-gray-700 font-semibold">Ixx:</span>
                        <span className="font-bold text-pink-700">{rolledTeeBarData.Ixx} cm⁴</span>
                      </div>
                      <div className="flex justify-between col-span-2 bg-pink-50 px-2 py-1 rounded mt-1">
                        <span className="text-gray-700 font-semibold">Iyy:</span>
                        <span className="font-bold text-pink-700">{rolledTeeBarData.Iyy} cm⁴</span>
                      </div>
                    </div>
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <p className="text-xs text-gray-500 italic">
                        Length will match beam: {beamLength.toFixed(2)} units
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Custom Section Parameters */}
          {sectionType === 'customSection' && (
            <div className="space-y-3 p-4 bg-gray-50 rounded border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">Custom Section Properties</h4>
              <p className="text-xs text-gray-600 mb-3">Enter mathematical values only (no visual representation)</p>
              
              <div className="space-y-3">
                {/* Moment of Inertia */}
                <div className="p-3 bg-white rounded border border-gray-200">
                  <h5 className="text-xs font-semibold text-gray-700 mb-2">Moment of Inertia</h5>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Ix</label>
                      <input
                        type="number"
                        value={customIx}
                        onChange={(e) => setCustomIx(parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Iy</label>
                      <input
                        type="number"
                        value={customIy}
                        onChange={(e) => setCustomIy(parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Iz</label>
                      <input
                        type="number"
                        value={customIz}
                        onChange={(e) => setCustomIz(parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                      />
                    </div>
                  </div>
                </div>

                {/* Cross-sectional Areas */}
                <div className="p-3 bg-white rounded border border-gray-200">
                  <h5 className="text-xs font-semibold text-gray-700 mb-2">Cross-sectional Areas</h5>
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Ax</label>
                      <input
                        type="number"
                        value={customAx}
                        onChange={(e) => setCustomAx(parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Ay</label>
                      <input
                        type="number"
                        value={customAy}
                        onChange={(e) => setCustomAy(parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Az</label>
                      <input
                        type="number"
                        value={customAz}
                        onChange={(e) => setCustomAz(parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                      />
                    </div>
                  </div>
                </div>

                {/* Geometric Properties */}
                <div className="p-3 bg-white rounded border border-gray-200">
                  <h5 className="text-xs font-semibold text-gray-700 mb-2">Geometric Properties</h5>
                  <div className="space-y-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Perimeter</label>
                      <input
                        type="number"
                        value={customPerimeter}
                        onChange={(e) => setCustomPerimeter(parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Width (optional)</label>
                        <input
                          type="number"
                          value={customWidth}
                          onChange={(e) => setCustomWidth(parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          placeholder="For rectangle"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">Depth (optional)</label>
                        <input
                          type="number"
                          value={customDepth}
                          onChange={(e) => setCustomDepth(parseFloat(e.target.value) || 0)}
                          min="0"
                          step="0.01"
                          placeholder="For rectangle"
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                        />
                      </div>
                    </div>

                    {/* Orientation selector (only shown when both width and depth are provided) */}
                    {customWidth > 0 && customDepth > 0 && (
                      <div className="mt-3 p-2 bg-blue-50 rounded border border-blue-200">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Rectangle Orientation
                        </label>
                        <div className="flex gap-4">
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              value="horizontal"
                              checked={customOrientation === 'horizontal'}
                              onChange={(e) => setCustomOrientation(e.target.value as 'horizontal' | 'vertical')}
                              className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-900">Horizontal</span>
                          </label>
                          <label className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="radio"
                              value="vertical"
                              checked={customOrientation === 'vertical'}
                              onChange={(e) => setCustomOrientation(e.target.value as 'horizontal' | 'vertical')}
                              className="w-4 h-4 text-blue-600 focus:ring-blue-500"
                            />
                            <span className="text-sm text-gray-900">Vertical</span>
                          </label>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">
                          Visual representation with {customWidth.toFixed(2)} × {customDepth.toFixed(2)} rectangle
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-2 pt-2 border-t border-gray-200">
                  <p className="text-xs text-gray-500 italic">
                    Length will match beam: {beamLength.toFixed(2)} units
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Cuboid Parameters */}
          {sectionType === 'cuboid' && (
            <div className="space-y-3 p-4 bg-gray-50 rounded border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">Cuboid Dimensions</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Length (L) - Auto (Beam Length)
                  </label>
                  <input
                    type="text"
                    value={beamLength.toFixed(3) + ' units'}
                    disabled
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-100 cursor-not-allowed text-gray-700"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Breadth (B)
                    </label>
                    <input
                      type="number"
                      value={breadth}
                      onChange={(e) => setBreadth(parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.01"
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Height (H)
                    </label>
                    <input
                      type="number"
                      value={height}
                      onChange={(e) => setHeight(parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.01"
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                    />
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Length matches beam, Breadth and Height are perpendicular cross-section
              </p>
            </div>
          )}

          {/* Cylinder Parameters */}
          {sectionType === 'cylinder' && (
            <div className="space-y-3 p-4 bg-gray-50 rounded border border-gray-200">
              <h4 className="text-sm font-medium text-gray-700">Cylinder Dimensions</h4>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Length (L) - Auto (Beam Length)
                  </label>
                  <input
                    type="text"
                    value={beamLength.toFixed(3) + ' units'}
                    disabled
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded bg-gray-100 cursor-not-allowed text-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Radius (R)
                  </label>
                  <input
                    type="number"
                    value={radius}
                    onChange={(e) => setRadius(parseFloat(e.target.value) || 0)}
                    min="0"
                    step="0.01"
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Length matches beam direction, Radius defines circular cross-section
              </p>
            </div>
          )}

          {/* Color Picker */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Section Color
            </label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="w-12 h-10 border border-gray-300 rounded cursor-pointer"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
              />
            </div>
          </div>

          {/* Orientation Controls */}
          <div className="space-y-3 p-4 bg-gray-50 rounded border border-gray-200">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Section Orientation</h4>
            
            {/* Row 1: Flip and Rotation buttons */}
            <div className="flex items-center gap-2">
              {/* Left-Right Flip */}
              <button
                type="button"
                onClick={() => setFlipHorizontal(!flipHorizontal)}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors flex items-center justify-center gap-1 ${
                  flipHorizontal 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
                title="Flip Left-Right"
              >
                <span className="text-base">⇄</span>
                <span>L-R Flip</span>
              </button>

              {/* Up-Down Flip */}
              <button
                type="button"
                onClick={() => setFlipVertical(!flipVertical)}
                className={`flex-1 px-3 py-2 text-xs font-medium rounded transition-colors flex items-center justify-center gap-1 ${
                  flipVertical 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                }`}
                title="Flip Up-Down"
              >
                <span className="text-base">⇅</span>
                <span>U-D Flip</span>
              </button>

              {/* Rotate +90° */}
              <button
                type="button"
                onClick={() => {
                  const newRotation = (rotation + 90) % 360;
                  setRotation(newRotation);
                  setCustomRotation(newRotation.toFixed(2));
                }}
                className="flex-1 px-3 py-2 text-xs font-medium bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
                title="Rotate +90 degrees"
              >
                <span className="text-base">↻</span>
                <span>+90°</span>
              </button>

              {/* Rotate -90° */}
              <button
                type="button"
                onClick={() => {
                  const newRotation = (rotation - 90 + 360) % 360;
                  setRotation(newRotation);
                  setCustomRotation(newRotation.toFixed(2));
                }}
                className="flex-1 px-3 py-2 text-xs font-medium bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
                title="Rotate -90 degrees"
              >
                <span className="text-base">↺</span>
                <span>-90°</span>
              </button>

              {/* Rotate +180° */}
              <button
                type="button"
                onClick={() => {
                  const newRotation = (rotation + 180) % 360;
                  setRotation(newRotation);
                  setCustomRotation(newRotation.toFixed(2));
                }}
                className="flex-1 px-3 py-2 text-xs font-medium bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
                title="Rotate +180 degrees"
              >
                <span className="text-base">⟲</span>
                <span>+180°</span>
              </button>

              {/* Rotate -180° */}
              <button
                type="button"
                onClick={() => {
                  const newRotation = (rotation - 180 + 360) % 360;
                  setRotation(newRotation);
                  setCustomRotation(newRotation.toFixed(2));
                }}
                className="flex-1 px-3 py-2 text-xs font-medium bg-white text-gray-700 border border-gray-300 rounded hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
                title="Rotate -180 degrees"
              >
                <span className="text-base">⟳</span>
                <span>-180°</span>
              </button>
            </div>

            {/* Row 2: Custom Rotation Input */}
            <div className="flex items-center gap-2">
              <label className="text-xs font-medium text-gray-600 whitespace-nowrap">
                Custom Rotation:
              </label>
              <input
                type="number"
                min="0"
                max="360"
                step="0.01"
                value={customRotation}
                onChange={(e) => {
                  const value = e.target.value;
                  setCustomRotation(value);
                  const numValue = parseFloat(value);
                  if (!isNaN(numValue) && numValue >= 0 && numValue <= 360) {
                    setRotation(numValue);
                  }
                }}
                className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900"
                placeholder="0.00 - 360.00"
              />
              <span className="text-xs text-gray-500">degrees</span>
              <button
                type="button"
                onClick={() => {
                  setRotation(0);
                  setCustomRotation('0');
                  setFlipHorizontal(false);
                  setFlipVertical(false);
                }}
                className="px-3 py-2 text-xs font-medium bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
                title="Reset orientation"
              >
                Reset
              </button>
            </div>

            {/* Current Orientation Display */}
            <div className="text-xs text-gray-500 flex items-center gap-2 pt-1 border-t border-gray-200">
              <span className="font-medium">Current:</span>
              <span>
                Rotation: {rotation.toFixed(2)}°
                {flipHorizontal && ' | H-Flip'}
                {flipVertical && ' | V-Flip'}
              </span>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
              {successMessage}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={handleCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleClear}
            className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded hover:bg-red-200 transition-colors"
          >
            Clear
          </button>
          <button
            onClick={handleApply}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded hover:bg-blue-700 transition-colors"
          >
            {hasExistingSection ? 'Update Section' : 'Apply Section'}
          </button>
        </div>
      </div>
    </div>
  );
};
